/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Grid, Typography, Divider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IQuestion,
	IQuestionnaireFormData,
	IQuestionnaireFormProps,
	IQuestionnaire,
	IQuestionnaireFormError,
	IValueSelect,
} from 'src/features/questionnaire/domain/types';
import {
	createQuestionnaireSubmission,
	getQuestionnaireSubmission,
} from 'src/features/questionnaire/store/questionnaireThunks';
import { AppDispatch } from 'src/store';
import { formatDateISO } from 'src/utils/dateUtils';

import widgetComponentMap from './questionnaire_form_widgets/WidgetComponentMap';

const QuestionnaireForm: React.FC<IQuestionnaireFormProps> = ({
	questionnaireId,
	isFormDisabled,
	compactMode = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const { studyId, siteSlug } = useParams<{
		studyId: string;
		siteSlug: string;
	}>();

	const formDataRef = useRef<IQuestionnaireFormData>({});
	const initialFormDataRef = useRef<IQuestionnaireFormData>({});
	const questionRefs = useRef<any>({});

	const [formErrors, setFormErrors] = useState<IQuestionnaireFormError>({});
	const [questionnaire, setQuestionnaire] = useState<IQuestionnaire | null>(
		null,
	);
	const [hasDisplayableQuestions, setHasDisplayableQuestions] =
		useState(false);
	const [wasQuestionnaireSaved, setWasQuestionnaireSaved] = useState({
		wasQuestionnaireSaved: false,
	});

	const submittedBy = useMemo(() => {
		if (!questionnaire?.submitted_by) return '';

		return `${questionnaire.submitted_by.first_name.trim()} ${questionnaire.submitted_by.last_name.trim()} 
		(${questionnaire.submitted_by.email.trim()})`;
	}, [questionnaire]);

	const handleValueChange = (
		question: IQuestion,
		value: any,
		type: string,
	) => {
		const oldFormData = formDataRef.current;

		const newFormData = {
			...oldFormData,
			[question.id]: {
				...oldFormData[question.id],
				[type]: value === '' || value === null ? null : value,
			},
		};
		formDataRef.current = newFormData;

		setFormErrors((prev) => {
			if (!(question.id in prev)) return prev;

			const IsMultiInputWidget =
				question.widget.startsWith('radiobutton_with_') ||
				question.widget.startsWith('check_box_with_');

			if (IsMultiInputWidget) {
				if (Array.isArray(value)) {
					const anyMissingText = value.some((item) => {
						const matchedOption = question.options.find(
							(opt) => opt.id === Number(item.value),
						);
						if (matchedOption?.allow_text) {
							return !item.text || item.text.trim() === '';
						}
						return false;
					});

					if (anyMissingText) {
						return prev;
					}
				} else if (typeof value === 'object' && value !== null) {
					const matchedOption = question.options.find(
						(opt) => opt.id === Number(value.value),
					);
					if (
						matchedOption?.allow_text &&
						(!value.text || value.text.trim() === '')
					) {
						return prev;
					}
				}
			}

			const newErrors = { ...prev };
			delete newErrors[question.id];
			return newErrors;
		});
	};

	const validateRequiredFields = (
		questionnaire: IQuestionnaire,
		formData: IQuestionnaireFormData,
	) => {
		const requiredFieldErrors: IQuestionnaireFormError = {};
		questionnaire.sections.forEach((section) => {
			section.questions.forEach((question) => {
				const questionData = formData[question.id];
				let hasValidValue = true;

				if (question.is_required) {
					hasValidValue =
						questionData &&
						Object.values(questionData).some((value) => {
							if (Array.isArray(value)) {
								return (
									value.length > 0 &&
									value.every((item) =>
										Object.values(item).some(
											(inner) =>
												inner !== null &&
												inner !== undefined &&
												inner !== '',
										),
									)
								);
							} else if (
								typeof value === 'object' &&
								value !== null
							) {
								return Object.values(value).some(
									(inner) =>
										inner !== null &&
										inner !== undefined &&
										inner !== '',
								);
							} else {
								return (
									value !== null &&
									value !== undefined &&
									value !== ''
								);
							}
						});
				}

				if (['single_select', 'multi_select'].includes(question.type)) {
					const valueData = questionData?.[
						`value_${question.type}`
					] as IValueSelect | IValueSelect[];

					if (Array.isArray(valueData) && valueData.length > 0) {
						valueData.forEach((entry) => {
							const matchedOption = question.options.find(
								(opt) => opt.id === Number(entry.value),
							);

							if (matchedOption?.allow_text) {
								const filledFields = Object.values(
									entry,
								).filter(
									(v) =>
										v !== null &&
										v !== undefined &&
										v !== '',
								);
								hasValidValue = filledFields.length >= 2;
							}
						});
					} else if (
						typeof valueData === 'object' &&
						valueData !== null
					) {
						const selectedValue = (valueData as IValueSelect)
							?.value;
						const matchedOption = question.options.find(
							(opt) => opt.id === Number(selectedValue),
						);

						if (matchedOption?.allow_text) {
							const filledFields = Object.values(
								valueData,
							).filter(
								(v) =>
									v !== null && v !== undefined && v !== '',
							);
							hasValidValue = filledFields.length >= 2;
						}
					}
				}

				if (!hasValidValue) {
					requiredFieldErrors[question.id] = {
						error: true,
						helperText: 'This field is required',
					};
				}
			});
		});

		return requiredFieldErrors;
	};

	const getFormattedQuestionnaireData = (
		questionnaire: IQuestionnaire,
		studyId: string,
		formData: IQuestionnaireFormData,
	) => {
		const formattedData = {
			questionnaire_id: questionnaire.id,
			study_id: Number(studyId),
			sections: questionnaire.sections.map((section) => ({
				section_id: section.id,
				questions: section.questions.flatMap((question) => {
					const questionData = formData[question.id];

					const hasValidData =
						questionData &&
						Object.values(questionData).some((value) => {
							if (Array.isArray(value)) {
								return value.every((item) =>
									Object.values(item).some(
										(innerValue) =>
											innerValue !== null &&
											innerValue !== undefined &&
											innerValue !== '',
									),
								);
							} else if (value && typeof value === 'object') {
								return Object.values(value).some(
									(innerValue) =>
										innerValue !== null &&
										innerValue !== undefined &&
										innerValue !== '',
								);
							} else {
								return (
									value !== null &&
									value !== undefined &&
									value !== ''
								);
							}
						});

					if (!hasValidData) return [];

					return {
						question_id: question.id,
						...questionData,
					};
				}),
			})),
		};

		return formattedData;
	};

	const handleSubmit = async () => {
		if (!studyId || !siteSlug || !questionnaire) return;
		const currentFormDataJSON = JSON.stringify(formDataRef.current);
		const initialFormDataJSON = JSON.stringify(initialFormDataRef.current);

		if (currentFormDataJSON === initialFormDataJSON) {
			snackbar('No changes detected to submit !', 'info', 'left');
			return;
		}

		const latestErrors = {
			...formErrors,
			...validateRequiredFields(questionnaire, formDataRef.current),
		};
		if (Object.keys(latestErrors).length > 0) {
			setFormErrors(latestErrors);
			snackbar('Errors detected on the form', 'warning', 'left');

			// auto scroll to first error on form
			const firstErrorKey = Object.keys(latestErrors)[0];
			const firstErrorElement = questionRefs.current[firstErrorKey];
			if (firstErrorElement) {
				firstErrorElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
				firstErrorElement.focus?.();
			}

			return;
		}

		const formattedData = getFormattedQuestionnaireData(
			questionnaire,
			studyId,
			formDataRef.current,
		);

		dispatch(
			createQuestionnaireSubmission({
				request: formattedData,
				siteSlug: siteSlug,
				onSuccess: () => {
					initialFormDataRef.current = formDataRef.current;
					setWasQuestionnaireSaved({ wasQuestionnaireSaved: true });
					snackbar(
						`${questionnaire.name} submitted successfully`,
						'success',
						'left',
					);
				},
			}),
		);
	};

	useEffect(() => {
		if (!studyId || !siteSlug) {
			return;
		}

		dispatch(
			getQuestionnaireSubmission({
				questionnaireId: questionnaireId,
				studyId: Number(studyId),
				siteSlug: siteSlug,
				onSuccess: (response) => {
					const hasQuestions = response.sections.some(
						(section) => section.questions.length > 0,
					);
					setHasDisplayableQuestions(hasQuestions);
					setQuestionnaire(response);
				},
			}),
		);
	}, []);

	useEffect(() => {
		if (!questionnaire) return;
		if (Object.keys(formDataRef.current).length > 0) return;

		const initialData: IQuestionnaireFormData = {};

		questionnaire.sections.forEach((section) => {
			section.questions.forEach((question) => {
				const { id, submitted_data } = question;

				if (!submitted_data) return;

				initialData[id] = {
					value_text: submitted_data.value_text ?? undefined,
					value_text_area:
						submitted_data.value_text_area ?? undefined,
					value_single_select:
						submitted_data.value_single_select ?? undefined,
					value_multi_select:
						submitted_data.value_multi_select &&
						submitted_data.value_multi_select.length > 0
							? submitted_data.value_multi_select
							: undefined,
					value_integer: submitted_data.value_integer ?? undefined,
					value_decimal: submitted_data.value_decimal ?? undefined,
					value_date: submitted_data.value_date
						? formatDateISO(submitted_data.value_date)
						: undefined,
					value_datetime: submitted_data.value_datetime ?? undefined,
					value_file: submitted_data.value_file ?? undefined,
					value_image: submitted_data.value_image ?? undefined,
				};
			});
		});

		formDataRef.current = initialData;
		initialFormDataRef.current = initialData;
	}, [questionnaire]);

	if (!hasDisplayableQuestions)
		return <Box sx={{ p: 1 }}>No questions data available.</Box>;

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box
				sx={{ width: compactMode ? '100%' : { xs: '100%', lg: '50%' } }}
			>
				{questionnaire &&
					questionnaire.sections.map((section, sectionIndex) => {
						const nextSection =
							questionnaire.sections[sectionIndex + 1];

						const showDivider =
							nextSection &&
							(!section.is_hidden || !nextSection.is_hidden);

						return (
							<React.Fragment
								key={`${sectionIndex}-${section.id}`}
							>
								<Box sx={{ mt: 1 }}>
									{section.display_name !== 'null' && (
										<Typography
											variant='h2'
											sx={{
												fontSize: compactMode
													? '0.9rem'
													: {
															xs: '0.9rem',
															sm: '1.2rem',
														},
												fontWeight: 'bold',
												marginBottom: '1rem',
											}}
										>
											{section.display_name}
										</Typography>
									)}
									<Grid
										container
										spacing={
											compactMode
												? 1
												: {
														xs: 1,
														sm: 2,
													}
										}
									>
										{section.questions.map((question) => {
											const FormWidget =
												widgetComponentMap[
													question.widget
												];

											if (!FormWidget) {
												console.warn(
													`No component found for type: ${question.widget}`,
												);
												return null;
											}

											return (
												<Grid
													item
													xs={12}
													md={12}
													key={`${sectionIndex}-${question.id}`}
												>
													<Box
														sx={{
															mb: 1,
															display: 'flex',
															alignItems:
																'flex-start',
														}}
													>
														{question.serial_number && (
															<Typography
																variant='subtitle2'
																sx={{
																	mr: 2,
																	width: '24px',
																	fontFamily:
																		'"Courier New", monospace',
																}}
															>
																{`${question.serial_number}.`}
															</Typography>
														)}
														<Box
															sx={{ flex: 1 }}
															ref={(el) => {
																if (el)
																	questionRefs.current[
																		question.id
																	] = el;
															}}
														>
															<FormWidget
																question={
																	question
																}
																isDisabled={
																	isFormDisabled
																}
																onValueChange={
																	handleValueChange
																}
																error={
																	formErrors[
																		question
																			.id
																	]?.error ||
																	false
																}
																helperText={
																	formErrors[
																		question
																			.id
																	]
																		?.helperText ||
																	''
																}
																compactMode={
																	compactMode
																}
																wasSubmitted={
																	wasQuestionnaireSaved
																}
															/>
														</Box>
													</Box>
												</Grid>
											);
										})}
									</Grid>
								</Box>

								{showDivider && (
									<Divider
										sx={{
											my: 2,
											borderBottomWidth: '2px',
											borderColor: '#BE3327',
										}}
									/>
								)}
							</React.Fragment>
						);
					})}
			</Box>
			<Box
				sx={{
					mt: 4,
					display: 'flex',
					alignItems: 'center',
					width: '100%',
				}}
			>
				{questionnaire?.submitted_by && compactMode && (
					<>
						<Divider
							sx={{
								my: 2,
								borderBottomWidth: '2px',
								borderColor: '#BE3327',
							}}
						/>
						<Typography
							variant='caption'
							sx={{
								fontSize: compactMode
									? '0.9rem'
									: {
											xs: '0.9rem',
											sm: '1.2rem',
										},
								color: 'rgba(0, 0, 0, 0.38)',
								fontWeight: '500',
							}}
						>
							Submitted By : {submittedBy}
						</Typography>
					</>
				)}

				{!isFormDisabled && (
					<PrimaryButton
						onClick={handleSubmit}
						fullWidth={false}
						sx={{
							ml: 'auto',
							backgroundColor: '#BE3327',
							'&:hover': {
								backgroundColor: '#A52D23',
							},
						}}
					>
						Save
					</PrimaryButton>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default QuestionnaireForm;
